"use client";

/**
 * MaterialsSummary3.jsx — FULL REPLACEMENT
 *
 * Changes from previous version:
 *   - Order-number CHIPS on each size variant are replaced with a real
 *     per-order sub-table: order #, vendor, qty ordered, qty delivered,
 *     qty pending, unit $, total $, and status — one row per Material
 *     Order that touched this size variant.
 *   - This makes it possible to see, at a glance, whether the SAME size
 *     of the SAME item was ordered across multiple purchase orders, and
 *     whether the combined quantity across those orders overshot what
 *     was actually needed (over-ordering across POs, not just
 *     over-delivery on a single order).
 *   - Size variant summary rows now also show "# orders" so you can spot
 *     a size that's been hit by 3+ separate POs at a glance before
 *     expanding further.
 *   - NEW: a view toggle — "By Item" (unchanged item→size→PO drill-down)
 *     vs "By Purchase Order" — which flips the hierarchy so you can pick
 *     a single PO and see every item on it with quantities, alongside a
 *     summary roll-up of that PO's totals. Both views share the same
 *     search/vendor/status filters and the same underlying data.
 *   - Everything else (grouping by itemCode, search/vendor/status
 *     filters, headline totals, null-safety) is unchanged from before.
 */

import { useMemo, useState } from "react";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// Safe lowercase — never throws on null/undefined
const sl = (v) => String(v ?? "").toLowerCase();

const STATUS_COLORS = {
  pending: "bg-yellow-700/60 text-yellow-200",
  confirmed: "bg-blue-700/60 text-blue-200",
  partial: "bg-orange-700/60 text-orange-200",
  delivered: "bg-green-700/60 text-green-200",
  void: "bg-red-800/60 text-red-300",
};

// Roll up multiple statuses into a single display label
function rollupStatus(statuses) {
  const set = new Set(statuses.filter(Boolean));
  if (set.size === 0) return "—";
  if (set.size === 1) return [...set][0];
  if (set.has("partial")) return "partial";
  if (set.has("delivered") && (set.has("pending") || set.has("confirmed")))
    return "partial";
  if ([...set].every((s) => s === "delivered")) return "delivered";
  if ([...set].every((s) => s === "void")) return "void";
  return "partial";
}

function StatusPill({ status }) {
  return (
    <span
      className={cls(
        "px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wide",
        STATUS_COLORS[status] || "bg-gray-700 text-gray-400",
      )}
    >
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-order sub-table shown inside a size variant row
// Shows exactly which POs contributed to this size's totals, and how much
// each one ordered — this is the "did we over-order across POs" view.
// ─────────────────────────────────────────────────────────────────────────────
function SizeOrderBreakdown({ sv }) {
  const overallOver = sv.qtyDelivered > sv.qtyOrdered && sv.qtyOrdered > 0;
  return (
    <div className="bg-black/20 border border-gray-700/50 rounded-lg overflow-hidden mt-1.5 mb-1">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-gray-700/40 bg-gray-900/50">
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              PO #
            </th>
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              MO #
            </th>
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              Vendor
            </th>
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              Ordered
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Qty
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Delivered
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Pending
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Unit $
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Total $
            </th>
            <th className="px-3 py-1.5 text-center text-gray-500 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800/60">
          {sv.orderLines.map((ol, i) => {
            const lineOver =
              ol.qtyDelivered > ol.qtyOrdered && ol.qtyOrdered > 0;
            const pending = Math.max(0, ol.qtyOrdered - ol.qtyDelivered);
            return (
              <tr key={i} className="hover:bg-gray-800/30 transition-colors">
                <td className="px-3 py-1.5">
                  <span className="font-mono text-orange-300 bg-orange-900/20 px-1.5 py-0.5 rounded border border-orange-700/30">
                    {ol.poNumber || "—"}
                  </span>
                  {ol.poTitle && (
                    <span className="block text-[10px] text-gray-500 mt-0.5 max-w-[140px] truncate">
                      {ol.poTitle}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <span className="font-mono text-gray-400 bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700">
                    {ol.orderNumber || "—"}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-400">
                  {ol.vendor !== "—" ? (
                    ol.vendor
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-gray-500">
                  {ol.orderedAt
                    ? new Date(ol.orderedAt).toLocaleDateString("en-US", {
                        timeZone: "UTC",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—"}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-gray-300">
                  {fmtQty(ol.qtyOrdered)}
                </td>
                <td
                  className={cls(
                    "px-3 py-1.5 text-right font-mono",
                    lineOver ? "text-orange-400 font-bold" : "text-green-400",
                  )}
                >
                  {fmtQty(ol.qtyDelivered)}
                  {lineOver && <span className="ml-1">⚠</span>}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-yellow-400">
                  {fmtQty(pending)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-gray-400">
                  ${fmt(ol.unitPrice)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-blue-300 font-semibold">
                  ${fmt(ol.total)}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <StatusPill status={ol.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t border-gray-700/40 bg-gray-900/60">
          <tr>
            <td colSpan={4} className="px-3 py-1.5 text-gray-500">
              {sv.orderLines.length} order line
              {sv.orderLines.length !== 1 ? "s" : ""} across {sv.uniquePOCount}{" "}
              PO{sv.uniquePOCount !== 1 ? "s" : ""} for this size
              {overallOver && (
                <span className="ml-2 text-orange-400 font-bold">
                  ⚠ over-ordered across POs
                </span>
              )}
            </td>
            <td className="px-3 py-1.5 text-right font-mono font-bold text-gray-200">
              {fmtQty(sv.qtyOrdered)}
            </td>
            <td
              className={cls(
                "px-3 py-1.5 text-right font-mono font-bold",
                overallOver ? "text-orange-400" : "text-green-300",
              )}
            >
              {fmtQty(sv.qtyDelivered)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono text-yellow-300">
              {fmtQty(Math.max(0, sv.qtyOrdered - sv.qtyDelivered))}
            </td>
            <td />
            <td className="px-3 py-1.5 text-right font-mono font-bold text-blue-200">
              ${fmt(sv.total)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drill-down panel for a single item group
// Shows each size variant with qty + expandable per-order breakdown
// ─────────────────────────────────────────────────────────────────────────────
function GroupDrilldown({ group }) {
  const [openSizes, setOpenSizes] = useState(new Set());

  const toggleSize = (key) => {
    setOpenSizes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="border-t border-gray-700/60 bg-gray-900/40">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-700/40">
              <th className="px-4 py-2 text-left text-gray-500 font-medium w-8" />
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Size / Spec
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                UOM
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Ordered
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Delivered
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Pending
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Unit $
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Total $
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Vendor(s)
              </th>
              <th className="px-4 py-2 text-center text-gray-500 font-medium">
                # POs
              </th>
              <th className="px-4 py-2 text-center text-gray-500 font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/30">
            {group.sizes.map((sv, i) => {
              const isOver =
                sv.qtyDelivered > sv.qtyOrdered && sv.qtyOrdered > 0;
              const pending = Math.max(0, sv.qtyOrdered - sv.qtyDelivered);
              const isOpen = openSizes.has(sv.key);
              const multiPO = sv.uniquePOCount > 1;
              return (
                <>
                  <tr
                    key={sv.key}
                    onClick={() => toggleSize(sv.key)}
                    className={cls(
                      "transition-colors cursor-pointer",
                      i % 2 === 0 ? "bg-gray-800/20" : "bg-transparent",
                      isOpen ? "bg-gray-700/30" : "",
                    )}
                  >
                    <td className="px-4 py-2 text-gray-500">
                      <span
                        className={cls(
                          "inline-block transition-transform text-sm",
                          isOpen ? "rotate-90" : "",
                        )}
                      >
                        ›
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-300">
                      {sv.size && sv.size !== "—" ? (
                        sv.size
                      ) : (
                        <span className="text-gray-600 italic">no size</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{sv.uom}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-300">
                      {fmtQty(sv.qtyOrdered)}
                    </td>
                    <td
                      className={cls(
                        "px-4 py-2 text-right font-mono",
                        isOver ? "text-orange-400 font-bold" : "text-green-400",
                      )}
                    >
                      {fmtQty(sv.qtyDelivered)}
                      {isOver && <span className="ml-1 text-[9px]">⚠</span>}
                    </td>
                    <td className="px-8 py-2 text-right font-mono text-yellow-400">
                      {fmtQty(pending)}
                    </td>
                    <td className="px-8 py-2 text-right font-mono text-gray-400">
                      ${fmt(sv.unitPrice)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-blue-300 font-semibold">
                      ${fmt(sv.total)}
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {sv.vendor !== "—" ? (
                        sv.vendor
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={cls(
                          "text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border",
                          multiPO
                            ? "text-orange-300 border-orange-700/50 bg-orange-900/30"
                            : "text-gray-400 border-gray-700 bg-gray-800",
                        )}
                      >
                        {sv.uniquePOCount}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <StatusPill status={sv.status} />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${sv.key}-detail`}>
                      <td colSpan={11} className="px-4 pb-2">
                        <SizeOrderBreakdown sv={sv} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
          {/* Size subtotals */}
          <tfoot className="border-t border-gray-700/40 bg-gray-900/60">
            <tr>
              <td colSpan={3} className="px-4 py-2 text-xs text-gray-500">
                {group.sizes.length} size variant
                {group.sizes.length !== 1 ? "s" : ""}
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-gray-200 text-xs">
                {fmtQty(group.totalQtyOrdered)}
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-green-300 text-xs">
                {fmtQty(group.totalQtyDelivered)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-yellow-300 text-xs">
                {fmtQty(
                  Math.max(0, group.totalQtyOrdered - group.totalQtyDelivered),
                )}
              </td>
              <td />
              <td className="px-4 py-2 text-right font-mono font-bold text-blue-200 text-xs">
                ${fmt(group.totalAmount)}
              </td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PO drill-down — groups every line on this Purchase Order by item + size,
// so it reads as "1/2" Plain — 80 ordered, $X total" instead of a raw list
// of every individual order line. Click a grouped row to see the MO(s) that
// made it up, since one PO can have several Material Orders drawn against it.
// ─────────────────────────────────────────────────────────────────────────────
function PODrilldown({ po }) {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (key) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group this PO's lines by item + size + uom + unit price + vendor.
  // Vendor is part of the key: the same item/size bought from two different
  // vendors on the same PO must stay as two separate rows, not merged.
  const itemGroups = useMemo(() => {
    const map = new Map();
    po.lines.forEach((li) => {
      const key = [
        sl(li.itemCode) || sl(li.description).slice(0, 40),
        sl(li.size) || "__none__",
        sl(li.uom),
        String(Math.round((li.unitPrice || 0) * 100)),
        sl(li.vendor),
      ].join("||");
      if (!map.has(key)) {
        map.set(key, {
          key,
          itemCode: li.itemCode,
          description: li.description,
          size: li.size,
          uom: li.uom,
          unitPrice: li.unitPrice,
          vendor: li.vendor,
          qtyOrdered: 0,
          qtyDelivered: 0,
          total: 0,
          moLines: [],
          statuses: [],
        });
      }
      const g = map.get(key);
      g.qtyOrdered += li.qtyOrdered;
      g.qtyDelivered += li.qtyDelivered;
      g.total += li.total;
      g.statuses.push(li.status);
      g.moLines.push(li);
    });
    return [...map.values()]
      .map((g) => ({ ...g, status: rollupStatus(g.statuses) }))
      .sort(
        (a, b) =>
          (b.qtyDelivered > b.qtyOrdered ? 1 : 0) -
            (a.qtyDelivered > a.qtyOrdered ? 1 : 0) ||
          (a.description || a.itemCode).localeCompare(
            b.description || b.itemCode,
          ),
      );
  }, [po.lines]);

  return (
    <div className="border-t border-gray-700/60 bg-gray-900/40">
      {po.moCount > 1 && (
        <div className="px-4 py-2 text-[11px] text-orange-300 bg-orange-900/20 border-b border-orange-700/30">
          This PO has been drawn on by {po.moCount} separate Material Orders:{" "}
          {po.moNumbers.join(", ")}
        </div>
      )}
      {po.hasMultipleVendors && (
        <div className="px-4 py-2 text-[11px] text-orange-300 bg-orange-900/20 border-b border-orange-700/30">
          This PO spans multiple vendors: {po.uniqueVendors.join(", ")}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-700/40">
              <th className="px-4 py-2 text-left text-gray-500 font-medium w-8" />
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Item Code
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Description
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Size
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                UOM
              </th>
              <th className="px-4 py-2 text-left text-gray-500 font-medium">
                Vendor
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Ordered
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Delivered
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Pending
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Unit $
              </th>
              <th className="px-4 py-2 text-right text-gray-500 font-medium">
                Total $
              </th>
              <th className="px-4 py-2 text-center text-gray-500 font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/30">
            {itemGroups.map((g, i) => {
              const isOver = g.qtyDelivered > g.qtyOrdered && g.qtyOrdered > 0;
              const pending = Math.max(0, g.qtyOrdered - g.qtyDelivered);
              const isOpen = openItems.has(g.key);
              const multiMO = g.moLines.length > 1;
              return (
                <>
                  <tr
                    key={g.key}
                    onClick={() => toggleItem(g.key)}
                    className={cls(
                      "transition-colors cursor-pointer",
                      i % 2 === 0 ? "bg-gray-800/20" : "bg-transparent",
                      isOpen ? "bg-gray-700/30" : "",
                    )}
                  >
                    <td className="px-4 py-2 text-gray-500">
                      {multiMO && (
                        <span
                          className={cls(
                            "inline-block transition-transform text-sm",
                            isOpen ? "rotate-90" : "",
                          )}
                        >
                          ›
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-orange-400">
                      {g.itemCode || (
                        <span className="text-gray-600 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-300 max-w-[220px] truncate">
                      {g.description || (
                        <span className="text-gray-600 italic">
                          No description
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-400">
                      {g.size && g.size !== "—" ? (
                        g.size
                      ) : (
                        <span className="text-gray-600 italic">no size</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{g.uom}</td>
                    <td className="px-4 py-2 text-gray-400">
                      {g.vendor && g.vendor !== "—" ? (
                        g.vendor
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-gray-200">
                      {fmtQty(g.qtyOrdered)}
                      {multiMO && (
                        <span className="ml-1 text-[9px] text-orange-300 font-normal">
                          ({g.moLines.length} MOs)
                        </span>
                      )}
                    </td>
                    <td
                      className={cls(
                        "px-4 py-2 text-right font-mono",
                        isOver ? "text-orange-400 font-bold" : "text-green-400",
                      )}
                    >
                      {fmtQty(g.qtyDelivered)}
                      {isOver && <span className="ml-1 text-[9px]">⚠</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-yellow-400">
                      {fmtQty(pending)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-gray-400">
                      ${fmt(g.unitPrice)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-blue-300 font-semibold">
                      ${fmt(g.total)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <StatusPill status={g.status} />
                    </td>
                  </tr>
                  {isOpen && multiMO && (
                    <tr key={`${g.key}-detail`}>
                      <td colSpan={12} className="px-4 pb-2">
                        <div className="bg-black/20 border border-gray-700/50 rounded-lg overflow-hidden mt-1 mb-1">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="border-b border-gray-700/40 bg-gray-900/50">
                                <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
                                  MO #
                                </th>
                                <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                                  Ordered
                                </th>
                                <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                                  Delivered
                                </th>
                                <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                                  Total $
                                </th>
                                <th className="px-3 py-1.5 text-center text-gray-500 font-medium">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800/60">
                              {g.moLines.map((ml, mi) => (
                                <tr key={mi}>
                                  <td className="px-3 py-1.5">
                                    <span className="font-mono text-gray-400 bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700">
                                      {ml.orderNumber || "—"}
                                    </span>
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono text-gray-300">
                                    {fmtQty(ml.qtyOrdered)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono text-green-400">
                                    {fmtQty(ml.qtyDelivered)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono text-blue-300">
                                    ${fmt(ml.total)}
                                  </td>
                                  <td className="px-3 py-1.5 text-center">
                                    <StatusPill status={ml.status} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
          {/* PO summary roll-up */}
          <tfoot className="border-t border-gray-700/40 bg-gray-900/60">
            <tr>
              <td colSpan={5} className="px-4 py-2 text-xs text-gray-500">
                {itemGroups.length} item
                {itemGroups.length !== 1 ? "s" : ""} on this PO
                {po.moCount > 1 && ` · ${po.moCount} Material Orders`}
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-gray-200 text-xs">
                {fmtQty(po.totalQtyOrdered)}
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold text-green-300 text-xs">
                {fmtQty(po.totalQtyDelivered)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-yellow-300 text-xs">
                {fmtQty(Math.max(0, po.totalQtyOrdered - po.totalQtyDelivered))}
              </td>
              <td />
              <td className="px-4 py-2 text-right font-mono font-bold text-blue-200 text-xs">
                ${fmt(po.totalAmount)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function MaterialsSummary3({ orders = [] }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [vendorFilter, setVendor] = useState("all");
  const [expandedGroups, setExpanded] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());
  const [viewMode, setViewMode] = useState("item"); // "item" | "po"

  // ── Flatten all order items ────────────────────────────────────────────────
  // Note: "orderNumber" below is the Material Order's own number, distinct
  // from the linked Purchase Order (poNumber + title). One PO can have
  // multiple Material Orders against it, which is exactly the case the
  // By-Purchase-Order view needs to surface.
  const allItems = useMemo(() => {
    const rows = [];
    (orders || []).forEach((o) => {
      (o.items || []).forEach((item) => {
        rows.push({
          orderNumber: String(o.orderNumber ?? ""), // MO number
          orderId: String(o._id ?? ""),
          orderStatus: String(o.status ?? ""),
          orderedAt: o.createdAt ?? null,
          vendor: String(o.vendor?.companyName ?? "—"),
          deliveredAt: o.deliveredAt ?? null,
          // Purchase Order identity — this is what the By-PO view groups on
          poId: String(o.purchaseOrder?._id ?? o.purchaseOrder ?? ""),
          poNumber: String(o.purchaseOrder?.poNumber ?? ""),
          poTitle: String(o.purchaseOrder?.title ?? ""),
          itemCode: String(item.itemCode ?? ""),
          description: String(item.description ?? ""),
          size: String(item.size ?? ""),
          uom: String(item.uom ?? ""),
          sectionNumber: String(item.sectionNumber ?? ""),
          bidArea: String(item.bidArea ?? ""),
          quantity: Number(item.quantity ?? 0),
          qtyDelivered: Number(item.qtyDelivered ?? 0),
          unitPrice: Number(item.unitPrice ?? 0),
          total: Number(item.total ?? 0),
          hasVariance: Boolean(item.hasVariance),
        });
      });
    });
    return rows;
  }, [orders]);

  // ── Unique filter options ─────────────────────────────────────────────────
  const vendors = useMemo(() => {
    const set = new Set(
      allItems.map((i) => i.vendor).filter((v) => v && v !== "—"),
    );
    return ["all", ...[...set].sort()];
  }, [allItems]);

  // ── Pre-filter (vendor + status) ─────────────────────────────────────────
  const preFiltered = useMemo(() => {
    return allItems.filter((item) => {
      if (statusFilter !== "all" && item.orderStatus !== statusFilter)
        return false;
      if (vendorFilter !== "all" && item.vendor !== vendorFilter) return false;
      return true;
    });
  }, [allItems, statusFilter, vendorFilter]);

  // ── Group by itemCode ─────────────────────────────────────────────────────
  // Each group has: itemCode, description, sizes[], totals
  // Each size now carries orderLines[] — one row per PO that touched it.
  const groups = useMemo(() => {
    const map = new Map(); // itemCode → group

    preFiltered.forEach((item) => {
      const key =
        item.itemCode.trim().toLowerCase() || sl(item.description).slice(0, 40);
      if (!map.has(key)) {
        map.set(key, {
          key,
          itemCode: item.itemCode,
          description: item.description,
          // sizes: Map keyed by "size||uom||vendor||unitPrice" to group identical variants
          sizesMap: new Map(),
          statuses: [],
        });
      }
      const group = map.get(key);

      // Size variant key
      const svKey = [
        sl(item.size) || "__none__",
        sl(item.uom),
        sl(item.vendor),
        String(Math.round(item.unitPrice * 100)), // cents to avoid float key issues
      ].join("||");

      if (!group.sizesMap.has(svKey)) {
        group.sizesMap.set(svKey, {
          key: svKey,
          size: item.size || "",
          uom: item.uom,
          vendor: item.vendor,
          unitPrice: item.unitPrice,
          qtyOrdered: 0,
          qtyDelivered: 0,
          total: 0,
          // orderLines: one entry per PURCHASE ORDER (orderNumber) touching
          // this exact size/uom/vendor/price variant — this is the per-PO
          // breakdown that replaces the old chip list.
          orderLinesMap: new Map(),
          statuses: [],
        });
      }
      const sv = group.sizesMap.get(svKey);
      sv.qtyOrdered += item.quantity;
      sv.qtyDelivered += item.qtyDelivered;
      sv.total += item.total;
      sv.statuses.push(item.orderStatus);
      group.statuses.push(item.orderStatus);

      // Aggregate by order number in case an order has multiple line items
      // matching the same size variant (rare, but keep it correct).
      const olKey = item.orderNumber || item.orderId;
      if (!sv.orderLinesMap.has(olKey)) {
        sv.orderLinesMap.set(olKey, {
          orderNumber: item.orderNumber,
          orderId: item.orderId,
          poNumber: item.poNumber,
          poTitle: item.poTitle,
          poId: item.poId,
          vendor: item.vendor,
          orderedAt: item.orderedAt,
          unitPrice: item.unitPrice,
          qtyOrdered: 0,
          qtyDelivered: 0,
          total: 0,
          statuses: [],
        });
      }
      const ol = sv.orderLinesMap.get(olKey);
      ol.qtyOrdered += item.quantity;
      ol.qtyDelivered += item.qtyDelivered;
      ol.total += item.total;
      ol.statuses.push(item.orderStatus);
    });

    // Finalise each group
    const result = [];
    for (const group of map.values()) {
      const sizes = [...group.sizesMap.values()].map((sv) => {
        const orderLines = [...sv.orderLinesMap.values()]
          .map((ol) => ({
            ...ol,
            status: rollupStatus(ol.statuses),
          }))
          // most recent PO first
          .sort(
            (a, b) => new Date(b.orderedAt || 0) - new Date(a.orderedAt || 0),
          );
        const uniquePOCount = new Set(
          orderLines.map((ol) => ol.poId || ol.poNumber),
        ).size;
        return {
          ...sv,
          orderLines,
          uniquePOCount,
          status: rollupStatus(sv.statuses),
        };
      });

      const totalQtyOrdered = sizes.reduce((s, sv) => s + sv.qtyOrdered, 0);
      const totalQtyDelivered = sizes.reduce((s, sv) => s + sv.qtyDelivered, 0);
      const totalAmount = sizes.reduce((s, sv) => s + sv.total, 0);
      const hasOverDelivery = sizes.some(
        (sv) => sv.qtyDelivered > sv.qtyOrdered && sv.qtyOrdered > 0,
      );
      // "Multiple POs" means multiple distinct Purchase Orders, not just
      // multiple Material Orders — two MOs against the same PO don't count.
      const hasMultiPOSize = sizes.some((sv) => sv.uniquePOCount > 1);
      const rolledStatus = rollupStatus(group.statuses);

      result.push({
        key: group.key,
        itemCode: group.itemCode,
        description: group.description,
        sizes,
        totalQtyOrdered,
        totalQtyDelivered,
        totalPending: Math.max(0, totalQtyOrdered - totalQtyDelivered),
        totalAmount,
        sizeCount: sizes.length,
        hasOverDelivery,
        hasMultiPOSize,
        status: rolledStatus,
        uniqueVendors: [
          ...new Set(
            sizes.map((sv) => sv.vendor).filter((v) => v && v !== "—"),
          ),
        ],
        uniqueOrderCount: new Set(
          sizes.flatMap((sv) =>
            sv.orderLines.map((ol) => ol.poId || ol.poNumber),
          ),
        ).size,
      });
    }

    // Sort: over-delivery flags first, then multi-PO sizes, then by description
    return result.sort((a, b) => {
      if (a.hasOverDelivery !== b.hasOverDelivery)
        return a.hasOverDelivery ? -1 : 1;
      if (a.hasMultiPOSize !== b.hasMultiPOSize)
        return a.hasMultiPOSize ? -1 : 1;
      return (a.description || a.itemCode).localeCompare(
        b.description || b.itemCode,
      );
    });
  }, [preFiltered]);

  // ── Search on groups ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.trim().toLowerCase();
    return groups.filter(
      (g) => sl(g.itemCode).includes(q) || sl(g.description).includes(q),
    );
  }, [groups, search]);

  // ── Group by Purchase Order ────────────────────────────────────────────────
  // Keyed by the actual Purchase Order (poId/poNumber), NOT the Material
  // Order number — a single PO can have several Material Orders placed
  // against it over time, and that's exactly the case this view needs to
  // roll up so over-ordering across MOs on the same PO is visible.
  const poGroups = useMemo(() => {
    const map = new Map(); // poId||poNumber → po group

    preFiltered.forEach((item) => {
      const key = item.poId || item.poNumber || `__no-po__${item.vendor}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          poNumber: item.poNumber,
          poTitle: item.poTitle,
          poId: item.poId,
          lines: [],
          moNumbers: new Set(),
          vendors: new Set(),
          statuses: [],
          earliestOrderedAt: item.orderedAt,
        });
      }
      const po = map.get(key);
      const pending = Math.max(0, item.quantity - item.qtyDelivered);
      po.lines.push({
        itemCode: item.itemCode,
        description: item.description,
        size: item.size,
        uom: item.uom,
        vendor: item.vendor, // this MO's vendor — can differ per line on the same PO
        qtyOrdered: item.quantity,
        qtyDelivered: item.qtyDelivered,
        unitPrice: item.unitPrice,
        total: item.total,
        orderNumber: item.orderNumber, // MO this line came in on
        orderedAt: item.orderedAt,
        status:
          pending <= 0 && item.quantity > 0
            ? "delivered"
            : item.qtyDelivered > 0
              ? "partial"
              : item.orderStatus,
      });
      if (item.orderNumber) po.moNumbers.add(item.orderNumber);
      if (item.vendor && item.vendor !== "—") po.vendors.add(item.vendor);
      po.statuses.push(item.orderStatus);
      if (
        item.orderedAt &&
        (!po.earliestOrderedAt ||
          new Date(item.orderedAt) < new Date(po.earliestOrderedAt))
      ) {
        po.earliestOrderedAt = item.orderedAt;
      }
    });

    const result = [];
    for (const po of map.values()) {
      const totalQtyOrdered = po.lines.reduce((s, l) => s + l.qtyOrdered, 0);
      const totalQtyDelivered = po.lines.reduce(
        (s, l) => s + l.qtyDelivered,
        0,
      );
      const totalAmount = po.lines.reduce((s, l) => s + l.total, 0);
      const uniqueItemCount = new Set(
        po.lines.map((l) => sl(l.itemCode) || sl(l.description)),
      ).size;
      const hasOverDelivery = po.lines.some(
        (l) => l.qtyDelivered > l.qtyOrdered && l.qtyOrdered > 0,
      );
      const uniqueVendors = [...po.vendors];

      result.push({
        key: po.key,
        poNumber: po.poNumber,
        poTitle: po.poTitle,
        poId: po.poId,
        // Display vendor: the single vendor if only one is present on this
        // PO; otherwise flag it explicitly rather than silently picking one.
        vendor:
          uniqueVendors.length === 1
            ? uniqueVendors[0]
            : uniqueVendors.length > 1
              ? `${uniqueVendors.length} vendors`
              : "—",
        uniqueVendors,
        hasMultipleVendors: uniqueVendors.length > 1,
        orderedAt: po.earliestOrderedAt,
        lines: po.lines,
        moNumbers: [...po.moNumbers],
        moCount: po.moNumbers.size,
        status: rollupStatus(po.statuses),
        totalQtyOrdered,
        totalQtyDelivered,
        totalPending: Math.max(0, totalQtyOrdered - totalQtyDelivered),
        totalAmount,
        uniqueItemCount,
        hasOverDelivery,
      });
    }

    // Most recent PO first
    return result.sort(
      (a, b) => new Date(b.orderedAt || 0) - new Date(a.orderedAt || 0),
    );
  }, [preFiltered]);

  // ── Search on PO groups (matches PO #, PO title, MO #, vendor, or item) ───
  const filteredPOs = useMemo(() => {
    if (!search.trim()) return poGroups;
    const q = search.trim().toLowerCase();
    return poGroups.filter(
      (po) =>
        sl(po.poNumber).includes(q) ||
        sl(po.poTitle).includes(q) ||
        po.uniqueVendors.some((v) => sl(v).includes(q)) ||
        po.moNumbers.some((m) => sl(m).includes(q)) ||
        po.lines.some(
          (l) => sl(l.itemCode).includes(q) || sl(l.description).includes(q),
        ),
    );
  }, [poGroups, search]);

  // ── Headline totals (from filtered groups) ────────────────────────────────
  const totals = useMemo(
    () => ({
      groups: filtered.length,
      totalOrdered: filtered.reduce((s, g) => s + g.totalAmount, 0),
      totalDelivered: filtered.reduce((s, g) => {
        // sum delivered $ per size (qtyDelivered × unitPrice)
        return (
          s + g.sizes.reduce((ss, sv) => ss + sv.qtyDelivered * sv.unitPrice, 0)
        );
      }, 0),
      overDeliveryCount: filtered.filter((g) => g.hasOverDelivery).length,
      multiPOCount: filtered.filter((g) => g.hasMultiPOSize).length,
    }),
    [filtered],
  );

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePO = (key) => {
    setExpandedPOs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* ── Headline cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          {
            label: "Item Groups",
            value: String(totals.groups),
            color: "text-white",
          },
          {
            label: "Total Ordered",
            value: `$${fmt(totals.totalOrdered)}`,
            color: "text-yellow-300",
            mono: true,
          },
          {
            label: "Total Delivered",
            value: `$${fmt(totals.totalDelivered)}`,
            color: "text-green-300",
            mono: true,
          },
          {
            label: "Over-Delivered",
            value: String(totals.overDeliveryCount),
            color:
              totals.overDeliveryCount > 0
                ? "text-orange-400"
                : "text-gray-500",
          },
          {
            label: "Sizes on Multiple POs",
            value: String(totals.multiPOCount),
            color:
              totals.multiPOCount > 0 ? "text-orange-400" : "text-gray-500",
          },
        ].map(({ label, value, color, mono }) => (
          <div
            key={label}
            className="bg-gray-800 rounded-xl border border-gray-700 px-4 py-3"
          >
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {label}
            </p>
            <p className={cls("text-xl font-bold", color, mono && "font-mono")}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* ── View toggle ── */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg overflow-hidden border border-gray-600 bg-gray-800 p-0.5">
          {[
            ["item", "By Item"],
            ["po", "By Purchase Order"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setViewMode(v)}
              className={cls(
                "px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                viewMode === v
                  ? "bg-orange-500 text-white"
                  : "text-gray-400 hover:text-gray-200",
              )}
            >
              {l}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {viewMode === "item"
            ? "Drill into an item to see its sizes and the POs behind them"
            : "Drill into a PO to see every item and quantity on it"}
        </span>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              viewMode === "item"
                ? "Search code or description…"
                : "Search PO #, vendor, code or description…"
            }
            className="pl-8 pr-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-56"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-base leading-none"
            >
              ×
            </button>
          )}
        </div>

        <select
          value={vendorFilter}
          onChange={(e) => setVendor(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
        >
          {vendors.map((v) => (
            <option key={v} value={v}>
              {v === "all" ? "All Vendors" : v}
            </option>
          ))}
        </select>

        <div className="flex rounded overflow-hidden border border-gray-600">
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["confirmed", "Confirmed"],
            ["partial", "Partial"],
            ["delivered", "Delivered"],
            ["void", "Void"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              className={cls(
                "px-2.5 py-1.5 text-xs font-medium transition-colors",
                statusFilter === v
                  ? "bg-orange-500 text-white"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600",
              )}
            >
              {l}
            </button>
          ))}
        </div>

        <span className="text-xs text-gray-500 ml-auto">
          {viewMode === "item"
            ? `${filtered.length} of ${groups.length} item group${groups.length !== 1 ? "s" : ""}`
            : `${filteredPOs.length} of ${poGroups.length} purchase order${poGroups.length !== 1 ? "s" : ""}`}
        </span>

        {viewMode === "item" && expandedGroups.size > 0 && (
          <button
            onClick={() => setExpanded(new Set())}
            className="text-xs text-gray-500 hover:text-gray-300 underline"
          >
            Collapse all
          </button>
        )}
        {viewMode === "po" && expandedPOs.size > 0 && (
          <button
            onClick={() => setExpandedPOs(new Set())}
            className="text-xs text-gray-500 hover:text-gray-300 underline"
          >
            Collapse all
          </button>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-gray-500 px-1">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          In progress
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
          Fully delivered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />⚠
          Over-delivered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-orange-300 font-mono font-bold text-[9px] border border-orange-700/50 bg-orange-900/30 px-1 rounded">
            2+
          </span>
          Size ordered on multiple POs
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-px bg-gray-500 inline-block" />
          Click row to see size / PO breakdown
        </span>
      </div>

      {/* ── Grouped table (By Item) ── */}
      {viewMode === "item" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-[32px_1fr_120px_80px_80px_80px_100px_100px] items-center px-4 py-2.5 bg-gray-900 border-b border-gray-700 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            <span />
            <span>Item / Description</span>
            <span className="text-right">Total Ordered</span>
            <span className="text-right">Delivered</span>
            <span className="text-right">Pending</span>
            <span className="text-right">Total $</span>
            <span>Sizes</span>
            <span className="text-center">Status</span>
          </div>

          {filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500 text-sm">
              {groups.length === 0
                ? "No material order items yet."
                : "No items match your filters."}
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {filtered.map((group) => {
                const isOpen = expandedGroups.has(group.key);
                return (
                  <div key={group.key}>
                    {/* Group row */}
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className={cls(
                        "w-full grid grid-cols-[32px_1fr_120px_80px_80px_80px_100px_100px] items-center px-4 py-3 text-left transition-colors hover:bg-gray-700/40",
                        isOpen ? "bg-gray-700/30" : "",
                        group.hasOverDelivery
                          ? "border-l-2 border-orange-500/70"
                          : group.hasMultiPOSize
                            ? "border-l-2 border-orange-500/30"
                            : "",
                      )}
                    >
                      {/* Chevron */}
                      <span
                        className={cls(
                          "text-gray-500 transition-transform inline-block text-sm",
                          isOpen ? "rotate-90" : "",
                        )}
                      >
                        ›
                      </span>

                      {/* Identity */}
                      <span className="min-w-0 pr-3">
                        <span className="flex items-center gap-2 flex-wrap">
                          {group.itemCode && (
                            <span className="text-[11px] font-mono text-orange-400 shrink-0">
                              {group.itemCode}
                            </span>
                          )}
                          {group.hasOverDelivery && (
                            <span className="text-[9px] bg-orange-900/50 text-orange-300 border border-orange-700/40 px-1.5 py-0.5 rounded font-bold">
                              OVER
                            </span>
                          )}
                          {!group.hasOverDelivery && group.hasMultiPOSize && (
                            <span className="text-[9px] bg-orange-900/30 text-orange-300 border border-orange-700/30 px-1.5 py-0.5 rounded font-bold">
                              {group.uniqueOrderCount} POs
                            </span>
                          )}
                        </span>
                        <span className="text-sm text-gray-200 block truncate mt-0.5">
                          {group.description || (
                            <span className="text-gray-500 italic">
                              No description
                            </span>
                          )}
                        </span>
                      </span>

                      {/* Total qty ordered */}
                      <span className="text-right font-mono text-gray-300 text-sm">
                        {fmtQty(group.totalQtyOrdered)}
                      </span>

                      {/* Total qty delivered */}
                      <span
                        className={cls(
                          "text-right font-mono text-sm",
                          group.hasOverDelivery
                            ? "text-orange-400 font-bold"
                            : "text-green-400",
                        )}
                      >
                        {fmtQty(group.totalQtyDelivered)}
                      </span>

                      {/* Pending */}
                      <span
                        className={cls(
                          "text-right font-mono text-sm mx-4",
                          group.totalPending > 0
                            ? "text-yellow-400"
                            : "text-gray-600",
                        )}
                      >
                        {fmtQty(group.totalPending)}
                      </span>

                      {/* Total $ */}
                      <span className="text-right font-mono font-semibold text-blue-300 text-sm">
                        ${fmt(group.totalAmount)}
                      </span>

                      {/* Size chips */}
                      <span className="flex flex-wrap gap-1 mx-4">
                        {group.sizes.slice(0, 3).map((sv, i) => (
                          <span
                            key={i}
                            className={cls(
                              "text-[9px] px-1.5 py-0.5 rounded font-mono border",
                              sv.uniquePOCount > 1
                                ? "bg-orange-900/30 text-orange-300 border-orange-700/40"
                                : "bg-gray-700 text-gray-400 border-gray-600",
                            )}
                          >
                            {sv.size || "—"}
                            {sv.uniquePOCount > 1 && (
                              <span className="ml-1 opacity-70">
                                ×{sv.uniquePOCount}
                              </span>
                            )}
                          </span>
                        ))}
                        {group.sizes.length > 3 && (
                          <span className="text-[9px] text-gray-600">
                            +{group.sizes.length - 3}
                          </span>
                        )}
                      </span>

                      {/* Status */}
                      <span className="text-center">
                        <StatusPill status={group.status} />
                      </span>
                    </button>

                    {/* Drill-down */}
                    {isOpen && <GroupDrilldown group={group} />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer totals */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-[32px_1fr_120px_80px_80px_80px_100px_100px] items-center px-4 py-3 bg-gray-900 border-t-2 border-gray-600 text-xs font-bold">
              <span />
              <span className="text-gray-400">
                {filtered.length} item{filtered.length !== 1 ? "s" : ""}
              </span>
              <span className="text-right font-mono text-gray-200">
                {fmtQty(filtered.reduce((s, g) => s + g.totalQtyOrdered, 0))}
              </span>
              <span className="text-right font-mono text-green-300">
                {fmtQty(filtered.reduce((s, g) => s + g.totalQtyDelivered, 0))}
              </span>
              <span className="text-right font-mono text-yellow-300 mx-2">
                {fmtQty(filtered.reduce((s, g) => s + g.totalPending, 0))}
              </span>
              <span className="text-right font-mono text-blue-200 px-2">
                ${fmt(totals.totalOrdered)}
              </span>
              <span />
              <span />
            </div>
          )}
        </div>
      )}

      {/* ── Grouped table (By Purchase Order) ── */}
      {viewMode === "po" && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-[32px_150px_1fr_110px_70px_60px_90px_90px_100px_100px] items-center px-4 py-2.5 bg-gray-900 border-b border-gray-700 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            <span />
            <span>PO # / Name</span>
            <span>Vendor</span>
            <span>Ordered On</span>
            <span className="text-center">MOs</span>
            <span className="text-right">Items</span>
            <span className="text-right">Total Qty</span>
            <span className="text-right">Delivered</span>
            <span className="text-right">Total $</span>
            <span className="text-center">Status</span>
          </div>

          {filteredPOs.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500 text-sm">
              {poGroups.length === 0
                ? "No material order items yet."
                : "No purchase orders match your filters."}
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {filteredPOs.map((po) => {
                const isOpen = expandedPOs.has(po.key);
                return (
                  <div key={po.key}>
                    {/* PO row */}
                    <button
                      onClick={() => togglePO(po.key)}
                      className={cls(
                        "w-full grid grid-cols-[32px_150px_1fr_110px_70px_60px_90px_90px_100px_100px] items-center px-4 py-3 text-left transition-colors hover:bg-gray-700/40",
                        isOpen ? "bg-gray-700/30" : "",
                        po.hasOverDelivery
                          ? "border-l-2 border-orange-500/70"
                          : po.moCount > 1
                            ? "border-l-2 border-orange-500/30"
                            : "",
                      )}
                    >
                      <span
                        className={cls(
                          "text-gray-500 transition-transform inline-block text-sm",
                          isOpen ? "rotate-90" : "",
                        )}
                      >
                        ›
                      </span>

                      <span className="min-w-0 pr-2">
                        <span className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-mono text-orange-400 bg-gray-900 px-1.5 py-0.5 rounded border border-gray-700">
                            {po.poNumber || "—"}
                          </span>
                          {po.hasOverDelivery && (
                            <span className="text-[9px] bg-orange-900/50 text-orange-300 border border-orange-700/40 px-1 py-0.5 rounded font-bold">
                              OVER
                            </span>
                          )}
                        </span>
                        {po.poTitle && (
                          <span className="block text-[11px] text-gray-400 truncate mt-0.5">
                            {po.poTitle}
                          </span>
                        )}
                      </span>

                      <span
                        className={cls(
                          "text-sm truncate pr-2",
                          po.hasMultipleVendors
                            ? "text-orange-300 font-semibold"
                            : "text-gray-300",
                        )}
                        title={
                          po.hasMultipleVendors
                            ? po.uniqueVendors.join(", ")
                            : undefined
                        }
                      >
                        {po.vendor !== "—" ? (
                          po.vendor
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </span>

                      <span className="text-xs text-gray-500">
                        {po.orderedAt
                          ? new Date(po.orderedAt).toLocaleDateString("en-US", {
                              timeZone: "UTC",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </span>

                      <span className="text-center">
                        <span
                          className={cls(
                            "text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border",
                            po.moCount > 1
                              ? "text-orange-300 border-orange-700/50 bg-orange-900/30"
                              : "text-gray-400 border-gray-700 bg-gray-800",
                          )}
                        >
                          {po.moCount}
                        </span>
                      </span>

                      <span className="text-right font-mono text-gray-300 text-sm">
                        {po.uniqueItemCount}
                        <span className="text-gray-600">
                          /{po.lines.length}
                        </span>
                      </span>

                      <span className="text-right font-mono text-gray-300 text-sm">
                        {fmtQty(po.totalQtyOrdered)}
                      </span>

                      <span
                        className={cls(
                          "text-right font-mono text-sm",
                          po.hasOverDelivery
                            ? "text-orange-400 font-bold"
                            : "text-green-400",
                        )}
                      >
                        {fmtQty(po.totalQtyDelivered)}
                      </span>

                      <span className="text-right font-mono font-semibold text-blue-300 text-sm">
                        ${fmt(po.totalAmount)}
                      </span>

                      <span className="text-center">
                        <StatusPill status={po.status} />
                      </span>
                    </button>

                    {/* Drill-down: every item/size line on this PO + summary */}
                    {isOpen && <PODrilldown po={po} />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer totals */}
          {filteredPOs.length > 0 && (
            <div className="grid grid-cols-[32px_150px_1fr_110px_70px_60px_90px_90px_100px_100px] items-center px-4 py-3 bg-gray-900 border-t-2 border-gray-600 text-xs font-bold">
              <span />
              <span className="text-gray-400 col-span-3">
                {filteredPOs.length} purchase order
                {filteredPOs.length !== 1 ? "s" : ""}
              </span>
              <span />
              <span className="text-right font-mono text-gray-300">
                {filteredPOs.reduce((s, p) => s + p.lines.length, 0)}
              </span>
              <span className="text-right font-mono text-gray-200">
                {fmtQty(filteredPOs.reduce((s, p) => s + p.totalQtyOrdered, 0))}
              </span>
              <span className="text-right font-mono text-green-300">
                {fmtQty(
                  filteredPOs.reduce((s, p) => s + p.totalQtyDelivered, 0),
                )}
              </span>
              <span className="text-right font-mono text-blue-200">
                ${fmt(filteredPOs.reduce((s, p) => s + p.totalAmount, 0))}
              </span>
              <span />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
